import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { differenceInYears, parse } from "date-fns";
import { useAtom } from "jotai";
import { currentStep } from "@/app/store";
import { useQuery } from "@tanstack/react-query";
import { useCustomerCpf } from "@/app/hooks/useCustomerCpf";
import { MaritalStatus } from "@/app/services/panService/getMaritalStatus";
import { panService } from "@/app/services/panService";
import { usePanContext } from "@/app/contexts/PanContext";
import { BasicSelect } from "@/view/components/BasicSelect";
import { BasicInput } from "@/view/components/BasicInput";
import { Button } from "@/view/components/ui/button";
import { phoneMask } from "@/app/utils/phoneFormater";
import masks from "@/app/utils/masks";
import { birthDateMask } from "@/app/utils/birthDateFormater";
import { formatDate } from "@/app/utils/formatDate";

interface PersonalInfoFormProps {
  visible?: boolean;
}

export const personalFormSchema = z.object({
  name: z.string().min(1, "* obrigatório"),
  birthDate: z
    .string()
    .min(1, "* obrigatório")
    .refine((f) => {
      const date = parse(f, "dd/MM/yyyy", new Date());
      return date <= new Date();
    }, "Data inválida")
    .refine((f) => {
      const date = parse(f, "dd/MM/yyyy", new Date());
      const diff = differenceInYears(new Date(), date);
      return diff >= 18 && diff <= 69;
    }, "A idade deve estar entre 18 e 69 anos")
    .transform((f) => parse(f, "dd/MM/yyyy", new Date()).toISOString()),
  cpf: z
    .string()
    .optional()
    .transform((v) => v?.replace(/\D/g, "") ?? ""),
  phone: z
    .string()
    .refine((f) => f.replace(/\D/g, "").length === 11, "* Telefone inválido")
    .transform((v) => v.replace(/\D/g, "")),
  email: z.string().min(1, "* obrigatório").email("email invalido"),
  maritalStatus: z.string().min(1, "Estado civil é obrigatório"),
});

type personalformData = z.infer<typeof personalFormSchema>;

export default function PersonalInfoForm({ visible }: PersonalInfoFormProps) {
  const [, setStep] = useAtom(currentStep);

  const { customer, setCustomer } = usePanContext();

  const { data: isCustomer } = useCustomerCpf(customer?.cpf);

  const personalForm = useForm<personalformData>({
    resolver: zodResolver(personalFormSchema),
  });

  const capitalize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const { data: dataMaritalStatus } = useQuery<MaritalStatus[]>({
    queryKey: ["maritalStatus"],
    queryFn: panService.getMaritalStatus,
  });

  const maritalStatusOptions =
    dataMaritalStatus?.map((status) => ({
      label: capitalize(status.label),
      value: status.value,
    })) || [];

  useEffect(() => {
    if (isCustomer) {
      personalForm.reset({
        name: isCustomer.name ?? "",
        cpf: masks.cpf(isCustomer.cpf ?? ""),
        birthDate: isCustomer.birthDate ? formatDate(isCustomer.birthDate) : "",
        email: isCustomer.email ?? "",
        maritalStatus: isCustomer.maritalStatus ?? "",
      });
    }
  }, [isCustomer, personalForm]);

  const {
    handleSubmit,
    formState: { errors },
  } = personalForm;

  const submitForm = (data: personalformData) => {
    setCustomer({
      ...customer,
      name: data.name,
      cpf: data.cpf,
      birthDate: data.birthDate,
      phone: data.phone,
      email: data.email,
      maritalStatus: data.maritalStatus,
    });

    setStep((state) => state + 1);
  };

  return (
    <div className={visible ? "flex flex-col gap-8" : "hidden"}>
      <FormProvider {...personalForm}>
        <div className="grid grid-cols-2 gap-4">
          <BasicInput
            name="name"
            label="Nome completo"
            error={errors.name?.message}
          />
          <BasicInput
            name="cpf"
            label="CPF"
            disabled
            error={errors.cpf?.message}
          />
          <BasicInput
            name="birthDate"
            label="Data de nascimento"
            error={errors.birthDate?.message}
            onInput={birthDateMask}
          />
          <BasicInput
            name="phone"
            label="Telefone"
            error={errors.phone?.message}
            onInput={phoneMask}
          />
          <BasicInput
            name="email"
            label="E-mail"
            error={errors.email?.message}
          />
          <BasicSelect
            name="maritalStatus"
            label="Estado civil"
            error={errors.maritalStatus?.message}
            options={maritalStatusOptions}
            placeholder="Selecione"
          />
        </div>
      </FormProvider>
      <div className="flex justify-end">
        <Button
          className="rounded-full px-8 py-3 font-semibold text-white"
          onClick={handleSubmit(submitForm)}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
